import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api } from "@/lib/data";

function newSession() {
    const s = `sc_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem("sc_session", s);
    return s;
}

export default function Concierge() {
    const [open, setOpen] = useState(false);
    const [sessionId] = useState(() => localStorage.getItem("sc_session") || newSession());
    const [messages, setMessages] = useState([
        { role: "assistant", content: "Welcome to Serenity Cove. I'm your AI concierge — ask me about our suites, spa, dining or Ocho Rios. How may I help you today?" },
    ]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    const send = async (e) => {
        e?.preventDefault?.();
        const text = input.trim();
        if (!text || busy) return;
        setMessages((m) => [...m, { role: "user", content: text }]);
        setInput("");
        setBusy(true);
        try {
            const { data } = await api.post("/concierge/chat", { session_id: sessionId, message: text });
            setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
        } catch (err) {
            setMessages((m) => [...m, { role: "assistant", content: "I apologise — our concierge line is briefly unavailable. Please try again in a moment." }]);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button
                data-testid="concierge-toggle"
                onClick={() => setOpen((s) => !s)}
                className="fixed bottom-24 right-4 md:right-6 z-40 w-14 h-14 rounded-full bg-navy text-ivory shadow-2xl shadow-navy/40 hover:scale-105 transition-transform flex items-center justify-center"
                aria-label="AI Concierge"
            >
                {open ? <X size={20} /> : <MessageCircle size={22} />}
                {!open && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-gold animate-marker-pulse" />
                )}
            </button>

            {open && (
                <div
                    data-testid="concierge-panel"
                    className="fixed bottom-40 right-3 md:right-6 z-40 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-ivory rounded-2xl shadow-2xl shadow-navy/40 overflow-hidden flex flex-col border border-navy/10"
                >
                    <div className="bg-navy text-ivory px-5 py-4 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-jade/40 flex items-center justify-center">
                            <Sparkles size={16} className="text-gold" />
                        </div>
                        <div>
                            <div className="font-display text-lg leading-none">AI Concierge</div>
                            <div className="text-[11px] text-ivory/60 mt-0.5 tracking-wide">Serenity Cove · always available</div>
                        </div>
                    </div>
                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-ivory">
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                data-testid={`concierge-msg-${m.role}`}
                                className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                                    m.role === "user"
                                        ? "ml-auto bg-navy text-ivory rounded-br-sm"
                                        : "bg-sand/40 text-navy rounded-bl-sm"
                                }`}
                            >
                                {m.content}
                            </div>
                        ))}
                        {busy && (
                            <div className="max-w-[60%] bg-sand/40 text-navy/60 px-4 py-3 rounded-2xl rounded-bl-sm text-sm">
                                composing a thoughtful reply…
                            </div>
                        )}
                    </div>
                    <form onSubmit={send} className="p-3 border-t border-navy/10 flex items-center gap-2 bg-white">
                        <input
                            data-testid="concierge-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask about rooms, dining, spa…"
                            className="flex-1 bg-transparent outline-none text-sm px-3 py-2"
                        />
                        <button data-testid="concierge-send" disabled={busy} className="btn-gold w-9 h-9 rounded-full flex items-center justify-center disabled:opacity-60">
                            <Send size={14} />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
